import { App } from '@capacitor/app';
import { Button, GhostButton, Logo, TextField } from '@mgh-app/component-library';
import { useAuth } from 'auth/useAuth';
import { ValidationErrors } from 'components/ValidationErrors';
import { useAppNavigation } from 'navigation/useAppNavigation';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { storeSignUpData } from 'slices/SignUpSlice';
import { SignInError } from 'services/UsersService';
import { useAppDispatch } from 'store';
import {
  display,
  fontSize,
  gridTemplateRows,
  justifyContent,
  margin,
  overflow,
  textAlign,
  textColor,
  twCls,
} from 'style';
import { EmailUtils } from 'utils/EmailUtils';

interface Fields {
  email: string;
  password: string;
}

type ErrorTypes = 'required' | 'pattern';

const ErrorHelper = {
  email: {
    required: 'Email Address is required',
    pattern: 'Email Address is incorrect',
  },
  password: {
    required: 'Password is required',
  },
};

export const Login = (): JSX.Element => {
  const { handleSubmit, control } = useForm<Fields>({ defaultValues: { email: '', password: '' } });
  const [customError, setCustomError] = useState<string[]>();
  const { signIn } = useAuth();
  const {
    navigateHome,
    navigateVerifyAccount,
    navigateRegistration,
    navigateForgotPassword,
    navigateBack,
    isHome,
    isLoginPage,
  } = useAppNavigation();
  const dispatch = useAppDispatch();

  useEffect(() => {
    App.addListener('backButton', () => {
      if (isLoginPage()) {
        App.minimizeApp();
      } else {
        navigateBack();
      }
    });

    return () => {
      App.removeAllListeners();
    };
  }, [isHome, isLoginPage, navigateBack]);

  const login = async ({ email, password }: Fields) => {
    try {
      await signIn(email, password);
      navigateHome();
    } catch (err) {
      const error = err as SignInError;
      if (error?.statusCode === 403) {
        dispatch(storeSignUpData({ email, password }));
        navigateVerifyAccount();
      } else if (Array.isArray(error?.message)) {
        setCustomError(error?.message);
      } else if (error?.message) {
        setCustomError([error.message]);
      } else {
        setCustomError(['Unknown error']);
      }
    }
  };

  return (
    <div
      className={twCls(
        display('grid'),
        gridTemplateRows('grid-rows-min-content-8'),
        overflow('overflow-auto'),
      )}
    >
      <Logo />
      <h1
        className={twCls(
          textColor('text-grey-900'),
          margin('mt-2'),
          textAlign('text-center'),
          fontSize('text-2xl'),
        )}
      >
        Login
      </h1>
      <Controller
        name="email"
        control={control}
        rules={{
          required: true,
          pattern: EmailUtils.EmailRegex,
        }}
        render={({ field, fieldState: { error } }) => (
          <TextField
            error={error !== undefined}
            helperText={ErrorHelper.email[error?.type as ErrorTypes]}
            className={twCls(margin('mt-28'))}
            label="Email Address"
            type="email"
            {...field}
          />
        )}
      />
      <Controller
        name="password"
        control={control}
        rules={{
          required: true,
        }}
        render={({ field, fieldState: { error } }) => (
          <TextField
            error={error !== undefined}
            helperText={error?.type === 'required' && ErrorHelper.password.required}
            className={twCls(margin('mt-9'))}
            label="Password"
            type="password"
            {...field}
          />
        )}
      />
      <GhostButton
        className={twCls(margin('mt-5'), justifyContent('justify-end'))}
        onClick={navigateForgotPassword}
      >
        Forgot Password?
      </GhostButton>
      <ValidationErrors errors={customError} className={twCls(margin('mt-10'))} />
      <Button className={twCls(margin('mt-4'))} onClick={handleSubmit(login)}>
        Continue
      </Button>
      <GhostButton className={twCls(margin('mt-16'))} onClick={navigateRegistration}>
        Create Account
      </GhostButton>
    </div>
  );
};
